import React from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { Button as ChakraButton, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import { typedSxMap } from "../../utils/typedSxMap";
import { buttonVariant } from "../Button/types";

export type DownloadOption = {
  label: string;
  onClick: () => void;
};

export interface DownloadMenuProps {
  options: DownloadOption[];
  buttonLabel: string;
  variants?: buttonVariant[];
  className?: string;
}

const styles = typedSxMap({
  menuList: {
    backgroundColor: "var(--color-input-background)",
    borderRadius: "var(--border-radius-2)",
    padding: "var(--m-xxxxs)",
    border: "1px solid var(--color-border)",
    outline: "none",
    display: "flex",
    flexDirection: "column",
    gap: "var(--m-xxxxs)",
    maxHeight: "50vh",
    overflowY: "auto",
    marginTop: "var(--m-xxxxs)",
    zIndex: 9999,
  },
  menuItem: {
    padding: "var(--m-xxs) var(--m-xs)",
    borderRadius: "var(--border-radius-2)",
    backgroundColor: "var(--color-background-menu-item)",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "&:active, &:focus, &:hover": {
      backgroundColor: "var(--color-background-menu-hover)",
    },
  },
});

export default function DownloadMenu({ options, buttonLabel, variants = ["secondary" as buttonVariant], className }: DownloadMenuProps) {
  const isSecondary = variants.includes("secondary");

  return (
    <Menu gutter={4} strategy="fixed">
      {({ isOpen }) => {
        // Create a new properly typed array for the variants
        const buttonVariants: buttonVariant[] = [...variants];

        // Add an 'active' variant when the menu is open
        if (isOpen) {
          buttonVariants.push("active" as buttonVariant);
        }

        return (
          <>
            <MenuButton
              as={ChakraButton}
              rightIcon={<ChevronDownIcon boxSize="17px" transform={isOpen ? "rotate(180deg)" : undefined} />}
              className={className}
              variant="unstyled"
              sx={{
                bg: isSecondary ? "var(--color-secondary)" : "var(--color-button)",
                color: isSecondary ? "var(--color-text-on-secondary)" : "var(--color-button-text)",
                borderRadius: "var(--border-radius-2)",
                padding: "0.5px var(--m-s)",
                fontWeight: 600,
                fontSize: "var(--font-size-xs)",
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                border: isSecondary ? "none" : "1px solid var(--color-button-border)",
                height: "auto",
                minHeight: "unset",
                lineHeight: "normal",
                _hover: {
                  bg: isSecondary ? "var(--color-secondary-hover)" : "var(--color-button-hover)",
                  color: isSecondary ? "var(--color-text-on-secondary)" : "var(--color-button-text)",
                },
                _active: {
                  bg: isSecondary ? "var(--color-secondary-hover)" : "var(--color-button-hover)",
                },
                "& .chakra-button__icon": {
                  marginInlineStart: "0.25rem",
                },
                paddingTop: "10px",
                paddingBottom: "10px",
              }}>
              <span style={{ padding: "var(--m-xxs) 0" }}>{buttonLabel}</span>
            </MenuButton>
            <MenuList
              sx={{
                ...styles.menuList,
                width: "160px",
                minWidth: "unset",
                maxWidth: "160px",
              }}>
              {options.map((option, index) => (
                <MenuItem key={index} onClick={option.onClick} sx={styles.menuItem}>
                  {option.label}
                </MenuItem>
              ))}
            </MenuList>
          </>
        );
      }}
    </Menu>
  );
}
